import toast, { toast as toastDismiss } from 'react-hot-toast';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Button, Grid, TextField, FormControlLabel, Switch, Typography, Tooltip, Autocomplete, Accordion, AccordionDetails, AccordionSummary, Breadcrumbs, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import { isFuture } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { userProfileContext } from '../../lib/UserProvider';
import usersApi from '../../server/usersApi';
import {
  TASK_STATUS,
  RECURRENCE_RULE_OPTIONS,
  DATE_RANGES,
  ORDER_BY_OPTIONS,
  ARTICLE_TYPES,
  TASK_TYPES,
  calculateNextScheduledTime,
  fetchMessageDetails,
  generateHistoryLinks
} from '../../components/tasks/helpers';

// Local Definitions from 'definitions' file
import {
  articleTypes,
  languages,              // Predefined set of language options
  lengths,                // Predefined set of length units
  temperatures,           // Predefined set of temperature units
  tones,                  // Predefined set of tones
  teams,                  // Predefined set of teams
  teamServiceTypeMapping, // Predefined set of services by team
  serviceTypes,           // Predefined set of service types
  models,                 // Predefined set of AI models
  publications,           // Predefined set of publication types
  printPublications,      // Predefined set of print publications
  empty,                   // Definition for an 'empty' state or selection
  booleans,
  publicationStatusOptions,
  getServiceTypes,
  tools
} from "../../definitions";

const NORMALISE = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, '')
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\s\-’'"]/gu, '')
    .trim();

const FNV1A = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

const subjectKeyFor = (s) => FNV1A(NORMALISE(s));

const CreateTaskPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user_id: userProfileId } = useContext(userProfileContext) || {};
  const [isNewArticleOptionsExpanded, setIsNewArticleOptionsExpanded] = useState(true);
  const [isAiOptionsExpanded, setIsAiOptionsExpanded] = useState(true);
  const [isPublicNoticesOptionsVisible, setIsPublicNoticesOptionsVisible] = useState(false);
  const [isSingleArticleOptionsVisible, setIsSingleArticleOptionsVisible] = useState(false);
  const [isTopicBurstOptionsVisible, setIsTopicBurstOptionsVisible] = useState(false);
  const [isWeatherArticleOptionsVisible, setIsWeatherArticleOptionsVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasUserChosenServiceType, setHasUserChosenServiceType] = useState(false);
  const toastShownRef = useRef(false);

  const publicNoticesGenerator = getServiceTypes(serviceTypes, {
    specificServiceType: "Public Notices Article Generator",
  })[0]?.value;

  const articleComposer = getServiceTypes(serviceTypes, {
    specificServiceType: "Article Composer",
  })[0]?.value;

  const newsArticleComposer = getServiceTypes(serviceTypes, {
    specificServiceType: "News Article Composer",
  })[0]?.value;

  const weatherArticle = getServiceTypes(serviceTypes, {
    specificServiceType: "Weather Article Composer (AccuWeather)",
  })[0]?.value;

  const NEWS_STORY_VALUE =
    articleTypes.find(t => (t.label || '').toLowerCase() === 'news story')?.value
    || 'news_story';

  const calculateInitialScheduledTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now;
  };

  // Define state for storing form input values
  const [task, setTask] = useState({
    id: 0,
    status: TASK_STATUS.PENDING,
    output: null,
    name: "",
    type: TASK_TYPES[0],
    created_at: "",
    updated_at: "",
    scheduled_time: calculateInitialScheduledTime(),
    user_id: userProfileId,
    input: {
      additionalData: {
        search: "",
        publications: [],
        dateRange: DATE_RANGES[3].value,
        category: ARTICLE_TYPES[0].value,
        orderBy: ORDER_BY_OPTIONS[0].value,
        subject: "",
        articleCount: 1
      },
      cmsData: {
        cms: "",
        cmsEntries: [],
        cmsPublicationStatus: "draft",
        cmsContentType: articleTypes[15].value,
        cmsConfig: {},
        tagSource: "publication"   // one of: "publication" | "ai" | "both"
      },
      options: {
        model: models[0].value,
        temperature: temperatures[4].value,
        max_length: lengths[3].value,
        messages: [],
        user: userProfileId,
        response_format: { "type": "json_object" },
        tool_choice: 'auto'
      },
      settings: {
        serviceType: publicNoticesGenerator,
        language: languages[0].value,
        articleTone: tones[0].value,
        writingStyle: "",
      }
    },
    recurring: false,
    recurrence_rule: RECURRENCE_RULE_OPTIONS.NONE,
    last_run_successful: null,
    last_run_at: "",
    next_run_at: "",
    auto_publish: "false"
  });

  useEffect(() => {
    setIsPublicNoticesOptionsVisible(task?.type?.includes('Public Notices'));
    setIsSingleArticleOptionsVisible(task?.type === 'Single Article');
    setIsTopicBurstOptionsVisible(task?.type === 'Topic Burst');
    setIsWeatherArticleOptionsVisible(task?.type === 'Weather Article');
  }, [task.type]);

  useEffect(() => {
    if (!toastShownRef.current && task?.output?.message) {
      if (task?.status === TASK_STATUS.COMPLETED || task?.last_run_successful === true) {
        toast.success('Last ' + task.output.message, {
          duration: 5000,
        });
      } else if (task?.status === TASK_STATUS.FAILED || task?.last_run_successful === false) {
        toast.error('Last Task ' + task.output.message, {
          duration: 5000,
        });
      }
      toastShownRef.current = true;
    }

    // Cleanup function to dismiss all toasts on unmount
    return () => {
      toastDismiss.dismiss();
    };
  }, [task?.status, task?.output?.message, task?.last_run_successful]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const statusOptions = Object.entries(TASK_STATUS).map(([key, value]) => ({ label: value, value }));
  const recurrenceOptions = Object.entries(RECURRENCE_RULE_OPTIONS).map(([key, value]) => ({ label: value, value: value }));

  // Inherited Publication Tone handling
  const INHERITED_TONE = 'inherited';
  const cmsEntriesSelected = task?.input?.cmsData?.cmsEntries || [];
  const multipleCmsSelected = cmsEntriesSelected.length > 1;

  // Build tone options dynamically (prepend "Inherited" when multiple CMS selected)
  const publicationToneOptions = multipleCmsSelected
    ? [{ label: 'Inherited (per CMS)', value: INHERITED_TONE }, ...publications]
    : publications;

  // Auto-apply/remove "Inherited" if user toggles between 1 vs many CMS (also on initial load)
  useEffect(() => {
    const cmsValues = task?.input?.cmsData?.cmsEntries || [];
    const multiple = cmsValues.length > 1;
    const currentStyle = task?.input?.settings?.writingStyle;

    if (multiple && !currentStyle) {
      setTask(prev => ({
        ...prev,
        input: {
          ...prev.input,
          settings: {
            ...prev.input.settings,
            writingStyle: INHERITED_TONE,
            articleTone: '' // clear to truly inherit
          }
        }
      }));
    } else if (!multiple && currentStyle === INHERITED_TONE) {
      const single = cmsValues[0] || '';
      setTask(prev => ({
        ...prev,
        input: {
          ...prev.input,
          settings: {
            ...prev.input.settings,
            writingStyle: single,
            articleTone: ''
          }
        }
      }));
    }
  }, [task?.input?.cmsData?.cmsEntries]);

  // If Article Type changes, set News default or fall back to Article Composer (no user override)
  useEffect(() => {
    const isArticleTask = task?.type === 'Single Article' || task?.type === 'Topic Burst';
    if (!isArticleTask || hasUserChosenServiceType) return;

    const currentType = task?.input?.cmsData?.cmsContentType;
    const currentService = task?.input?.settings?.serviceType;

    if (currentType === NEWS_STORY_VALUE) {
      if (newsArticleComposer && currentService !== newsArticleComposer) {
        setTask(prev => ({
          ...prev,
          input: {
            ...prev.input,
            settings: { ...prev.input.settings, serviceType: newsArticleComposer },
          },
        }));
      }
    } else {
      // Not a News Story: if we're still on News Article Composer, revert to Article Composer
      if (currentService === newsArticleComposer && articleComposer) {
        setTask(prev => ({
          ...prev,
          input: {
            ...prev.input,
            settings: { ...prev.input.settings, serviceType: articleComposer },
          },
        }));
      }
    }
  }, [
    task?.input?.cmsData?.cmsContentType,
    task?.input?.settings?.serviceType,
    task?.type,
    hasUserChosenServiceType,
    newsArticleComposer,
    articleComposer,
  ]);

  const handleNewArticleOptionsToggle = () => {
    setIsNewArticleOptionsExpanded(prevState => !prevState);
  };

  const handleAiOptionsToggle = () => {
    setIsAiOptionsExpanded(prevState => !prevState);
  };

  const validateFields = () => {
    const errors = {};
    if (!task?.name) errors.name = 'Task name is required';
    if (!task?.type) errors.type = 'Task type is required';
    if (!task?.status) errors.status = 'Task status is required';
    if (!task?.recurrence_rule) errors.recurrence_rule = 'Recurrence rule is required';
    if (!task?.scheduled_time) errors.scheduled_time = 'Scheduled start time is required';
    if (!task?.input?.cmsData?.cmsEntries?.length) errors.cmsEntries = 'CMS is required';
    if (!task?.input?.cmsData?.cmsContentType) errors.cmsContentType = 'Article type is required';
    if (!task?.input?.cmsData?.cmsPublicationStatus) errors.cmsPublicationStatus = 'Publication status is required';

    if (task?.type?.includes('Public Notices')) {
      if (!task?.input?.additionalData?.publications?.length) errors.publications = 'At least one publication is required for Public Notices';
      if (!task?.input?.additionalData?.dateRange) errors.dateRange = 'Date range is required for Public Notices';
      if (!task?.input?.additionalData?.orderBy) errors.orderBy = 'Order by is required for Public Notices';
      if (!task?.input?.additionalData?.category) errors.category = 'Category is required for Public Notices';
    }

    if (task?.type === 'Single Article') {
      if (!task?.input?.additionalData?.subject?.trim()) errors.subject = 'Subject is required for Single Article';
    }

    if (task?.type === 'Topic Burst') {
      const subject = task?.input?.additionalData?.subject?.trim();
      const count = Number(task?.input?.additionalData?.articleCount);
      if (!subject) errors.subject = 'Subject is required for Topic Burst';
      if (!count || count < 1 || count > 500) errors.articleCount = 'Choose 1–500 articles';
    }

    if (task?.type === 'Weather Article') {
      if (!task?.input?.additionalData?.publications?.length) errors.publications = 'At least one publication is required for Weather Notices';
    }

    return errors;
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const normalisedType = task.type === 'Unique Article' ? 'Single Article' : task.type;

      const payload = {
        ...task,
        type: normalisedType,
        input: {
          ...task.input,
          options: {
            ...task.input.options,
            tool_choice: task?.input?.options?.tool_choice || 'auto'
          },
          additionalData: {
            ...task.input.additionalData,
            articleCount: normalisedType === 'Single Article'
              ? 1
              : Math.max(1, Math.min(500, Number(task?.input?.additionalData?.articleCount || 1))),
            subject: (task?.input?.additionalData?.subject || '').toString().trim()
          }
        },
        output: null,
        progress: 0
      };

      if (location.state && location.state.task && location.state.task.id) {
        await usersApi.updateUserTask(userProfileId, location.state.task.id, payload);
        toast.success('Task updated successfully!');
      } else {
        await usersApi.createTask(userProfileId, payload);
        toast.success('Task created successfully!');
      }

      navigate(`/tasks/`);
    } catch (error) {
      console.error('Failed to save task:', error);
      toast.error('Failed to save task. Please try again.');
    }
  };

  // Load task data from location state when component mounts
  useEffect(() => {
    if (location.state && location.state.task) {
      const data = location.state.task;
      setTask(prevTask => {
        try {
          const parsedInput = JSON.parse(data.input || '{}');
          const coercedType = data.type === 'Unique Article' ? 'Single Article' : data.type;

          return {
            ...prevTask,
            ...data,
            type: coercedType,
            input: {
              subject: "",
              articleCount: 1,
              tool_choice: 'auto',
              ...parsedInput,
              additionalData: {
                subject: "",
                articleCount: coercedType === 'Single Article' ? 1 : Math.max(1, Math.min(500, Number((parsedInput.additionalData || {}).articleCount || 1))),
                ...(parsedInput.additionalData || {})
              },
              options: {
                tool_choice: (parsedInput.options || {}).tool_choice || 'auto',
                ...(parsedInput.options || {})
              }
            },
            output: JSON.parse(data.output || '{}'),
            scheduled_time: prevTask?.scheduled_time ?? calculateInitialScheduledTime()
          };
        } catch (error) {
          console.error('Error parsing JSON:', error);
          return {
            ...prevTask,
            ...data,
            type: data.type === 'Unique Article' ? 'Single Article' : data.type,
            scheduled_time: calculateInitialScheduledTime()
          };
        }
      });
    } else if (location.state && location.state.input) {
      setTask(prevTask => ({
        ...prevTask,
        type: location?.state?.type === 'Unique Article' ? 'Single Article' : (location?.state?.type || prevTask.type),
        input: {
          ...prevTask.input,
          ...location.state.input,
          additionalData: {
            subject: "",
            articleCount: (location?.state?.type === 'Single Article') ? 1 : Math.max(1, Math.min(500, Number((location.state.input.additionalData || {}).articleCount || 1))),
            ...((location.state.input || {}).additionalData || {})
          },
          options: {
            tool_choice: ((location.state.input || {}).options || {}).tool_choice || 'auto',
            ...((location.state.input || {}).options || {})
          }
        }
      }));
    }
  }, [location.state]);

  // Function to handle updating task state including arrays for multi-select
  const handleUpdateTask = (field, value, nestedField = null) => {
    setTask(prevTask => {
      if (nestedField) {
        let updatedInput = { ...prevTask.input };
        const updatedNested = {
          ...prevTask.input[nestedField],
          [field]: value
        };

        // Handle array updates specifically for multi-select fields like 'publications'
        if (Array.isArray(value)) {
          updatedNested[field] = value.map(item => item.printTitle || item.value);
        }

        // Reset the dependent field if 'articleTone' or 'writingStyle' is changed
        if (field === 'writingStyle') {
          updatedNested.articleTone = '';  // Reset articleTone when writingStyle is set
        } else if (field === 'articleTone') {
          updatedNested.writingStyle = ''; // Reset writingStyle when articleTone is set
        }

        // If updating CMS, also update associated print publications
        if (field === 'cmsEntries') {
          const selectedUrls = value.map(item => item.url);
          const associatedPrintPublications = printPublications.filter(pub =>
            selectedUrls.includes(pub.url)
          );
          updatedInput.additionalData.publications = associatedPrintPublications.map(pub => pub.printTitle);

          // ensure cmsEntries are stored as values, not objects
          updatedNested[field] = value.map(item => item.value);

          // default/clear "Publication Tone" depending on multi-select
          const newCmsValues = updatedNested[field]; // array of strings (cms keys)
          const becameMultiple = newCmsValues.length > 1;

          const currentSettings = updatedInput.settings || prevTask.input.settings || {};
          const currentStyle = currentSettings.writingStyle;

          if (becameMultiple) {
            // Only default to inherited if the user hasn't already picked a tone
            if (!currentStyle) {
              updatedInput.settings = {
                ...currentSettings,
                writingStyle: INHERITED_TONE,
                articleTone: '' // clear tone so we truly inherit per CMS
              };
            }
          } else {
            // If we drop back to a single CMS and we were "Inherited", snap to that single CMS
            if (currentStyle === INHERITED_TONE) {
              updatedInput.settings = {
                ...currentSettings,
                writingStyle: newCmsValues[0] || '',
                articleTone: ''
              };
            }
          }
        }

        // Handle array updates specifically for the 'messages' field
        if (field === 'messages') {
          const userMessage = { role: 'user', content: value };
          updatedNested.messages = [userMessage];
        }

        // If Subject changes, clear the per-subject history bucket(s) so Avoid list resets
        if (nestedField === 'additionalData' && field === 'subject') {
          const newSubject = (value || '').toString().trim();
          const newKey = subjectKeyFor(newSubject);
          const cmsEntries = (updatedInput.cmsData?.cmsEntries || []).filter(Boolean);

          // Ensure usedTopicsByCms exists and is object-shaped
          const ad = updatedInput.additionalData || {};
          const usedTopicsByCms =
            ad.usedTopicsByCms && typeof ad.usedTopicsByCms === 'object'
              ? { ...ad.usedTopicsByCms }
              : {};

          // For each selected CMS, reset only the new subject bucket
          cmsEntries.forEach(cms => {
            const perSubjectMap =
              usedTopicsByCms[cms] && typeof usedTopicsByCms[cms] === 'object'
                ? { ...usedTopicsByCms[cms] }
                : {};
            perSubjectMap[newKey] = []; // clear avoid/history for this subject on this CMS
            usedTopicsByCms[cms] = perSubjectMap;
          });

          updatedInput = {
            ...updatedInput,
            additionalData: {
              ...ad,
              usedTopicsByCms // persist the cleared buckets alongside other additionalData
            }
          };
        }

        return {
          ...prevTask,
          input: {
            ...updatedInput,
            [nestedField]: updatedNested
          }
        };
      } else if (field in prevTask.input) {
        // Handle updates directly within the 'input' object
        const updatedInput = {
          ...prevTask.input,
          [field]: value
        };

        return {
          ...prevTask,
          input: updatedInput
        };
      } else {
        const updatedTask = {
          ...prevTask,
          [field]: value
        };

        // Reset relevant fields based on the new task type
        if (field === 'type') {
          const newType = value === 'Unique Article' ? 'Single Article' : value;

          // Reset Public Notices options
          if (newType.includes('Public Notices')) {
            updatedTask.input.settings.serviceType = publicNoticesGenerator;
            updatedTask.input.additionalData.dateRange = DATE_RANGES[3].value;
            updatedTask.input.additionalData.category = ARTICLE_TYPES[0].value;
            updatedTask.input.additionalData.orderBy = ORDER_BY_OPTIONS[0].value;
          } else {
            updatedTask.input.additionalData.dateRange = '';
            updatedTask.input.additionalData.category = '';
            updatedTask.input.additionalData.orderBy = '';
          }

          // Single Article options
          if (newType === 'Single Article') {
            updatedTask.input.settings.serviceType = articleComposer;
            updatedTask.input.options.messages = [];
            updatedTask.input.additionalData.subject ??= '';
            updatedTask.input.additionalData.articleCount = 1;
          } else {
            // status doesn't apply to Single Article
          }

          // Topic Burst options
          if (newType === 'Topic Burst') {
            updatedTask.input.settings.serviceType = articleComposer;
            updatedTask.input.additionalData.subject ??= '';
            if (!updatedTask.input.additionalData.articleCount || updatedTask.input.additionalData.articleCount < 1) {
              updatedTask.input.additionalData.articleCount = 1;
            }
            updatedTask.input.options.messages = [];
          } else {
            // status doesn't apply to Topic Burst
          }

          // Weather Article options
          if (newType === 'Weather Article') {
            updatedTask.input.settings.serviceType = weatherArticle;
            updatedTask.input.additionalData.publications = [];
          } else {
            // status doesn't apply to Weather Article
          }

          // apply migrated type to task
          updatedTask.type = newType;

          // Auto-set scheduled start time to current time plus 5 minutes
          updatedTask.scheduled_time = calculateInitialScheduledTime();
        }

        // Handle scheduled_time updates
        if (field === 'scheduled_time') {
          const isScheduledTimeInFuture = isFuture(new Date(value));
          if (updatedTask.recurrence_rule === RECURRENCE_RULE_OPTIONS.NONE) {
            if (isScheduledTimeInFuture) {
              updatedTask.next_run_at = value;
              updatedTask.status = TASK_STATUS.PENDING;
            }
          } else {
            updatedTask.next_run_at = value;
            updatedTask.status = TASK_STATUS.TIMED;
          }
        }

        // Handle recurrence_rule updates
        if (field === 'recurrence_rule') {
          updatedTask.recurring = value !== RECURRENCE_RULE_OPTIONS.NONE;
          if (value !== RECURRENCE_RULE_OPTIONS.NONE) {
            updatedTask.status = TASK_STATUS.TIMED;
            const now = new Date();
            updatedTask.scheduled_time = now;
            updatedTask.next_run_at = calculateNextScheduledTime(now, value);
          } else if (value === RECURRENCE_RULE_OPTIONS.NONE && updatedTask.status === TASK_STATUS.TIMED) {
            updatedTask.status = TASK_STATUS.DISABLED;
            updatedTask.scheduled_time = null;
            updatedTask.next_run_at = null;
          }
        }

        return updatedTask;
      }
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} to="/tasks" color="inherit">
              Tasks
            </Link>
            <Typography color="textPrimary">{location.state && location.state.task ? 'Edit Task' : 'Create Task'}</Typography>
          </Breadcrumbs>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>{'Task Management'}</Typography>{task?.output?.messages?.length > 0 && (
              <Grid item>
                <Tooltip title="View Generated Articles">
                  <IconButton
                    color="primary"
                    onClick={async () => {
                      const sortedMessages = await fetchMessageDetails(task.output.messages, userProfileId);
                      toast.custom((t) => generateHistoryLinks(sortedMessages, t.id), {
                        duration: 90000,
                      });
                    }}
                  >
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
          <Typography variant="subtitle1" gutterBottom>
            Fill in the details below to {location.state && location.state.task ? 'edit' : 'create'} a task.
          </Typography>
          <form onSubmit={handleSubmit}>

            {/* Create / Edit Task */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Enter the name of the task">
                  <TextField
                    size="small"
                    label="Task Name"
                    variant="outlined"
                    fullWidth
                    value={task.name}
                    onChange={(e) => handleUpdateTask('name', e.target.value)}
                    error={!!validationErrors.name}
                    helperText={validationErrors.name}
                    required
                  />
                </Tooltip>
              </Grid>

              {/* Multi-select Autocomplete for Task Type */}
              <Grid item xs={12} md={6}>
                <Tooltip title="Select one or more task types">
                  <Autocomplete
                    size="small"
                    value={task.type}
                    onChange={(event, newValue) => handleUpdateTask('type', newValue)}
                    options={TASK_TYPES}
                    getOptionLabel={(option) => option} // Assuming the option is a simple string
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Task Type"
                        fullWidth
                        error={!!validationErrors.type}
                        helperText={validationErrors.type}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Task Status */}
              <Grid item xs={12} md={6}>
                <Tooltip title="Select the status of the task">
                  <Autocomplete
                    size="small"
                    value={statusOptions.find(status => status.value === task.status)}
                    onChange={(event, newValue) => handleUpdateTask('status', newValue ? newValue.value : '')}
                    options={statusOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Task Status"
                        fullWidth
                        error={!!validationErrors.status}
                        helperText={validationErrors.status}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Task Recurrence Rule */}
              <Grid item xs={12} md={6}>
                <Tooltip title="Select the recurrence rule for the task">
                  <Autocomplete
                    size="small"
                    value={recurrenceOptions.find(option => option.value === task.recurrence_rule) || null}
                    onChange={(event, newValue) => {
                      handleUpdateTask('recurrence_rule', newValue ? newValue.value : RECURRENCE_RULE_OPTIONS.NONE);
                    }}
                    options={recurrenceOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Task Recurrence Rule"
                        fullWidth
                        error={!!validationErrors.recurrence_rule}
                        helperText={validationErrors.recurrence_rule}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12}>
                <Tooltip title="Select the scheduled start time for the task">
                  <DateTimePicker
                    label="Scheduled Start"
                    value={task.scheduled_time || null}
                    onChange={(newValue) => handleUpdateTask('scheduled_time', newValue)}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        error={!!validationErrors.scheduled_time}
                        helperText={validationErrors.scheduled_time}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Add the CMS Autocomplete field for multiple selections */}
              <Grid item xs={12} md={6}>
                <Tooltip title="Select one or more CMS publications">
                  <Autocomplete
                    multiple
                    size="small"
                    value={publications.filter(pub => task?.input?.cmsData?.cmsEntries?.includes(pub.value))}
                    onChange={(event, newValue) => handleUpdateTask('cmsEntries', newValue, 'cmsData')}
                    options={publications}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="CMS"
                        fullWidth
                        error={!!validationErrors.cmsEntries}
                        helperText={validationErrors.cmsEntries}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Article Type */}
              <Grid item xs={12} md={6}>
                <Tooltip title="Select the article type">
                  <Autocomplete
                    size="small"
                    value={articleTypes.find(type => type.value === task?.input?.cmsData?.cmsContentType) || null}
                    onChange={(event, newValue) => handleUpdateTask('cmsContentType', newValue ? newValue.value : '', 'cmsData')}
                    options={articleTypes}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Article Type"
                        fullWidth
                        error={!!validationErrors.cmsContentType}
                        helperText={validationErrors.cmsContentType}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Publication Status */}
              <Grid item xs={12} md={6}>
                <Tooltip title="Select the publication status">
                  <Autocomplete
                    size="small"
                    value={publicationStatusOptions.find(status => status.value === task?.input?.cmsData?.cmsPublicationStatus) || null}
                    onChange={(event, newValue) => handleUpdateTask('cmsPublicationStatus', newValue ? newValue.value : 'Draft', 'cmsData')}
                    options={publicationStatusOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Publication Status"
                        fullWidth
                        error={!!validationErrors.cmsPublicationStatus}
                        helperText={validationErrors.cmsPublicationStatus}
                        required
                      />
                    )}
                  />
                </Tooltip>
              </Grid>

              {/* Push to CMS */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={task.auto_publish === 'true'} onChange={(e) => handleUpdateTask('auto_publish', e.target.checked ? 'true' : 'false')} />}
                  label="Push to CMS"
                  labelPlacement="start"
                />
              </Grid>

              {/* Public Notices Options */}
              {isPublicNoticesOptionsVisible && (
                <Grid item xs={12}>
                  <Accordion
                    expanded={isNewArticleOptionsExpanded}
                    onChange={handleNewArticleOptionsToggle}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" gutterBottom>Public Notices Article Options</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Tooltip title="Select one or more Print publications">
                            <Autocomplete
                              multiple
                              size="small"
                              value={printPublications.filter(pub => (task?.input?.additionalData?.publications || []).includes(pub.printTitle))}
                              onChange={(event, newValue) => handleUpdateTask('publications', newValue, 'additionalData')}
                              options={printPublications}
                              getOptionLabel={(option) => option.printTitle}
                              isOptionEqualToValue={(option, value) => option.printTitle === value.printTitle}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Print Publications"
                                  fullWidth
                                  error={!!validationErrors.publications}
                                  helperText={validationErrors.publications}
                                  required
                                />
                              )}
                            />
                          </Tooltip>
                        </Grid>
                        {/* Date Range */}
                        <Grid item xs={12} md={6}>
                          <Tooltip title="Select the date range">
                            <Autocomplete
                              size="small"
                              value={DATE_RANGES.find(range => range.value === task?.input?.additionalData?.dateRange) || null}
                              onChange={(event, newValue) => handleUpdateTask('dateRange', newValue ? newValue.value : '', 'additionalData')}
                              options={DATE_RANGES}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) => option.value === value.value}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Date Range"
                                  fullWidth
                                  error={!!validationErrors.dateRange}
                                  helperText={validationErrors.dateRange}
                                  required
                                />
                              )}
                            />
                          </Tooltip>
                        </Grid>

                        {/* Results Order */}
                        <Grid item xs={12} md={6}>
                          <Tooltip title="Select how you want the results ordered">
                            <Autocomplete
                              size="small"
                              value={ORDER_BY_OPTIONS.find(option => option.value === task?.input?.additionalData?.orderBy) || null}
                              onChange={(event, newValue) => handleUpdateTask('orderBy', newValue ? newValue.value : '', 'additionalData')}
                              options={ORDER_BY_OPTIONS}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) => option.value === value.value}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Order By"
                                  fullWidth
                                  error={!!validationErrors.orderBy}
                                  helperText={validationErrors.orderBy}
                                  required
                                />
                              )}
                            />
                          </Tooltip>
                        </Grid>

                        {/* PNP Article Type */}
                        <Grid item xs={12} md={6}>
                          <Tooltip title="Select the article Category">
                            <Autocomplete
                              size="small"
                              value={ARTICLE_TYPES.find(type => type.value === task?.input?.additionalData?.category) || null}
                              onChange={(event, newValue) => handleUpdateTask('category', newValue ? newValue.value : '', 'additionalData')}
                              options={ARTICLE_TYPES}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) => option.value === value.value}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Article Category"
                                  fullWidth
                                  error={!!validationErrors.category}
                                  helperText={validationErrors.category}
                                  required
                                />
                              )}
                            />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}

              {/* Single Article / Topic Burst Options */}
              {(isSingleArticleOptionsVisible || isTopicBurstOptionsVisible) && (
                <Grid item xs={12}>
                  <Accordion
                    expanded={isNewArticleOptionsExpanded}
                    onChange={handleNewArticleOptionsToggle}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" gutterBottom>{task?.type} Options</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={task?.type === 'Topic Burst' ? 8 : 12}>
                          <Tooltip title="Enter the subject">
                            <TextField
                              size="small"
                              label="Subject"
                              variant="outlined"
                              fullWidth
                              value={task?.input?.additionalData?.subject || ''}
                              onChange={(event) => handleUpdateTask('subject', event.target.value, 'additionalData')}
                              error={!!validationErrors.subject}
                              helperText={validationErrors.subject}
                              required
                            />
                          </Tooltip>
                        </Grid>
                        {task?.type === 'Topic Burst' && (
                          <Grid item xs={12} md={4}>
                            <Tooltip title="Number of articles to generate (1–500)">
                              <TextField
                                size="small"
                                label="How many?"
                                type="number"
                                inputProps={{ min: 1, max: 500 }}
                                fullWidth
                                value={task?.input?.additionalData?.articleCount ?? 1}
                                onChange={(event) => {
                                  const v = Math.max(1, Math.min(500, Number(event.target.value || 1)));
                                  handleUpdateTask('articleCount', v, 'additionalData');
                                }}
                                error={!!validationErrors.articleCount}
                                helperText={validationErrors.articleCount}
                                required
                              />
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}

              {/* Weather Article Options */}
              {isWeatherArticleOptionsVisible && (
                <Grid item xs={12}>
                  <Accordion
                    expanded={isNewArticleOptionsExpanded}
                    onChange={handleNewArticleOptionsToggle}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" gutterBottom>Weather Article Options</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Tooltip title="Select one or more Print publications">
                            <Autocomplete
                              multiple
                              size="small"
                              value={printPublications.filter(pub => (task?.input?.additionalData?.publications || []).includes(pub.printTitle))}
                              onChange={(event, newValue) => handleUpdateTask('publications', newValue, 'additionalData')}
                              options={printPublications}
                              getOptionLabel={(option) => option.printTitle}
                              isOptionEqualToValue={(option, value) => option.printTitle === value.printTitle}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Print Publications"
                                  fullWidth
                                  error={!!validationErrors.publications}
                                  helperText={validationErrors.publications}
                                  required
                                />
                              )}
                            />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}


              {/* New AI Options section */}
              <Grid item xs={12}>
                <Accordion
                  expanded={isAiOptionsExpanded}
                  onChange={handleAiOptionsToggle}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" gutterBottom>AI Options</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>

                      {/* Service Type */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the Service Type">
                          <Autocomplete
                            size="small"
                            value={serviceTypes.find(option => option.value === task?.input?.settings?.serviceType) || null}
                            onChange={(event, newValue) => {
                              setHasUserChosenServiceType(true); // user override
                              handleUpdateTask('serviceType', newValue ? newValue.value : '', 'settings');
                            }}
                            options={serviceTypes}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Services" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Tooltip title="Pick a tool. Auto will choose the best tool for you.">
                          <Autocomplete
                            size="small"
                            value={tools.find(t => t.value === (task?.input?.options?.tool_choice || 'auto')) || tools.find(t => t.value === 'auto')}
                            onChange={(event, newValue) => handleUpdateTask('tool_choice', newValue ? newValue.value : 'auto', 'options')}
                            options={tools}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Tool" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      {/* Language selection */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the language">
                          <Autocomplete
                            size="small"
                            value={languages.find(option => option.value === task?.input?.settings?.language) || null}
                            onChange={(event, newValue) => handleUpdateTask('language', newValue ? newValue.value : '', 'settings')}
                            options={languages}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Language" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      {/* Model selection */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the model">
                          <Autocomplete
                            size="small"
                            value={models.find(option => option.value === task?.input?.options?.model) || null}
                            onChange={(event, newValue) => handleUpdateTask('model', newValue ? newValue.value : '', 'options')}
                            options={models}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Model" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      {/* Temperature selection */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the temperature">
                          <Autocomplete
                            size="small"
                            value={temperatures.find(option => option.value === task?.input?.options?.temperature) || null}
                            onChange={(event, newValue) => handleUpdateTask('temperature', newValue ? newValue.value : '', 'options')}
                            options={temperatures}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Temperature" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      {/* Length selection */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the length">
                          <Autocomplete
                            size="small"
                            value={lengths.find(option => option.value === task?.input?.options?.max_length) || null}
                            onChange={(event, newValue) => handleUpdateTask('max_length', newValue ? newValue.value : 500, 'options')}
                            options={lengths}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Length" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      {/* Tone selection */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the tone">
                          <Autocomplete
                            size="small"
                            value={tones.find(option => option.value === task?.input?.settings?.articleTone) || null}
                            onChange={(event, newValue) => handleUpdateTask('articleTone', newValue ? newValue.value : '', 'settings')}
                            options={tones}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField {...params} label="Tone" fullWidth />
                            )}
                          />
                        </Tooltip>
                      </Grid>

                      {/* Publication Tone selection */}
                      <Grid item xs={12} md={6}>
                        <Tooltip title="Select the publication tone">
                          <Autocomplete
                            size="small"
                            value={
                              publicationToneOptions.find(
                                option => option.value === task?.input?.settings?.writingStyle
                              ) || null
                            }
                            onChange={(event, newValue) => handleUpdateTask('writingStyle', newValue ? newValue.value : '', 'settings')}
                            options={publicationToneOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Publication Tone"
                                fullWidth
                                helperText={
                                  multipleCmsSelected &&
                                    task?.input?.settings?.writingStyle === INHERITED_TONE
                                    ? 'Currently inheriting tone from each selected CMS. Pick a tone to override.'
                                    : ''
                                }
                              />
                            )}
                          />
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Add more fields for other task properties if needed */}
              <Grid item xs={12}>
                <Button size="small" type="submit" variant="contained" color="primary" onClick={handleSubmit}>
                  {location.state && location.state.task ? 'Update Task' : 'Create Task'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid >
    </LocalizationProvider >
  );
};

export default CreateTaskPage;
